import { useState, useMemo, useCallback, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import {
  EIDPSource,
  type IIDPFactInputData,
  type IIDPMissingField,
} from '@/modules/idp/types';
import { transformFactsToFields } from '@/modules/idp/utils/field-transformer';
import { extractFileIdFromSource } from '@/shared/utils/file-utils';
import {
  useIDPPanelStore,
  selectIDPPanelOpen,
  selectIDPPanelClose,
} from '@/shared/stores';

const IDPLayover = lazy(() =>
  import('@/modules/idp/components/IDPLayover').then(m => ({
    default: m.default,
  })),
);

/**
 * Options for the useIDPLayover hook.
 */
interface IUseIDPLayoverOptions {
  /** IDP data for the exception */
  idpData?: IIDPFactInputData | null;
  /** Callback to save edited fields (optional - required for editable mode) */
  onSave?: (editedValues: Record<string, string>) => Promise<boolean>;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Whether the layover should be read-only */
  readOnly?: boolean;
  /**
   * When true, opens the IDP inline panel (via store) instead of a portal layover.
   * Use false for contexts that need the portal (e.g., guidance center).
   * @default true
   */
  useInlinePanel?: boolean;
}

/**
 * Return type for the useIDPLayover hook.
 */
interface IUseIDPLayoverReturn {
  /** Whether the IDP layover is currently shown */
  showIdpView: boolean;
  /** Function to open the IDP layover, optionally with specific idpData override and read-only flag */
  openIdpView: (
    overrideData?: IIDPFactInputData | null,
    readOnlyOverride?: boolean,
  ) => void;
  /** Function to close the IDP layover */
  closeIdpView: () => void;
  /** Transformed exception fields for rendering */
  exceptionFields: IIDPMissingField[];
  /** Extracted document file ID */
  documentFileId: string;
  /** Portal element to render in the component (returns null when useInlinePanel is true) */
  renderLayoverPortal: () => React.ReactNode;
}

/**
 * Hook that manages IDP Layover state and provides portal rendering.
 *
 * Centralizes the IDP layover logic that was previously duplicated across:
 * - RunAssistant.tsx
 * - ResolvedExceptionItem.tsx
 * - QuestionCard.tsx
 *
 * When `useInlinePanel` is true (default), opens the IDP inline panel via
 * the Zustand store instead of rendering a portal layover. This allows the
 * panel to render alongside existing content (Quill chat or run cards).
 *
 * When `useInlinePanel` is false, falls back to the portal layover behavior
 * (used in contexts like the guidance center that don't have inline panel support).
 *
 * @param options - Hook options
 * @returns IDP layover state and render function
 */
export function useIDPLayover({
  idpData,
  onSave,
  isSaving = false,
  readOnly = false,
  useInlinePanel = true,
}: IUseIDPLayoverOptions): IUseIDPLayoverReturn {
  // Portal mode state (only used when useInlinePanel is false)
  const [showIdpViewLocal, setShowIdpViewLocal] = useState(false);
  const [overrideIdpData, setOverrideIdpData] = useState<
    IIDPFactInputData | null | undefined
  >(undefined);
  const [perClickReadOnly, setPerClickReadOnly] = useState(false);

  // Store selectors (only used when useInlinePanel is true)
  const openPanelStore = useIDPPanelStore(selectIDPPanelOpen);
  const closePanelStore = useIDPPanelStore(selectIDPPanelClose);

  const effectiveIdpData = overrideIdpData ?? idpData;

  const openIdpView = useCallback(
    (overrideData?: IIDPFactInputData | null, readOnlyOverride?: boolean) => {
      const dataToUse = overrideData ?? idpData;
      const isRO =
        !!readOnlyOverride ||
        readOnly ||
        dataToUse?.context === 'analyze_outputs' ||
        dataToUse?.context === 'manual_action_required';

      if (useInlinePanel) {
        const sourceValue = dataToUse?.source || '';
        const docFileId = extractFileIdFromSource(sourceValue);

        openPanelStore({
          source: EIDPSource.EXCEPTION,
          idpData: dataToUse,
          documentName: docFileId,
          readOnly: isRO,
          context: dataToUse?.context,
          exceptionFields: transformFactsToFields(dataToUse),
          onSave: isRO ? undefined : onSave,
        });
      } else {
        if (overrideData !== undefined) {
          setOverrideIdpData(overrideData);
        }
        setPerClickReadOnly(!!readOnlyOverride);
        setShowIdpViewLocal(true);
      }
    },
    [idpData, readOnly, useInlinePanel, openPanelStore, onSave],
  );

  const closeIdpView = useCallback(() => {
    if (useInlinePanel) {
      closePanelStore();
    } else {
      setShowIdpViewLocal(false);
      setOverrideIdpData(undefined);
      setPerClickReadOnly(false);
    }
  }, [useInlinePanel, closePanelStore]);

  const exceptionFields = useMemo(
    () => transformFactsToFields(idpData),
    [idpData],
  );

  const documentFileId = useMemo(() => {
    if (!effectiveIdpData) return '';
    const sourceValue = effectiveIdpData.source || '';
    return extractFileIdFromSource(sourceValue);
  }, [effectiveIdpData]);

  const isReadOnly = useMemo(() => {
    if (readOnly || perClickReadOnly) return true;
    return (
      effectiveIdpData?.context === 'analyze_outputs' ||
      effectiveIdpData?.context === 'manual_action_required'
    );
  }, [readOnly, perClickReadOnly, effectiveIdpData?.context]);

  /**
   * Renders the IDP Layover in a portal.
   * Returns null when useInlinePanel is true (panel is rendered by the page layout).
   */
  const renderLayoverPortal = useCallback(() => {
    if (useInlinePanel) return null;
    if (!showIdpViewLocal || !effectiveIdpData) return null;

    return createPortal(
      <Suspense fallback={null}>
        <IDPLayover
          isOpen={showIdpViewLocal}
          onClose={closeIdpView}
          documentName={documentFileId}
          fields={[]}
          source={EIDPSource.EXCEPTION}
          exceptionFields={exceptionFields}
          idpData={effectiveIdpData}
          onSave={isReadOnly ? undefined : onSave}
          isSaving={isSaving}
          readOnly={isReadOnly}
        />
      </Suspense>,
      document.body,
    );
  }, [
    useInlinePanel,
    showIdpViewLocal,
    effectiveIdpData,
    closeIdpView,
    documentFileId,
    exceptionFields,
    onSave,
    isSaving,
    isReadOnly,
  ]);

  return {
    showIdpView: useInlinePanel ? false : showIdpViewLocal,
    openIdpView,
    closeIdpView,
    exceptionFields,
    documentFileId,
    renderLayoverPortal,
  };
}

export type { IUseIDPLayoverOptions, IUseIDPLayoverReturn };
